import { apiErrorMessage, isApiError } from '@agos/api-client'
import { postAuthLogin } from '@agos/api-client/auth'
import { postFiskalStorno } from '@agos/api-client/fiskal'
import {
  postOrdersOrderIdConfirmStatus,
  postOrdersOrderIdReturnToKitchen,
} from '@agos/api-client/orders'
import { T, tt } from '../i18n'
import { formatCents } from '../money'

import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Download,
  Eye,
  EyeOff,
  Loader2,
  RotateCcw,
  Star,
  X,
} from 'lucide-react'
import { useRef, useState, type FormEvent } from 'react'

import { useModalFocus } from '../hooks/use-modal-focus'

/**
 * A local class joiner. `packages/ui` owns the real `cn`, and shared may not
 * import ui (§5) — this modal needs three conditional classes, not a design
 * system.
 */
function cn(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(' ')
}
import { Link } from 'wouter'

import {
  ROLES_THAT_MAY_CONFIRM,
  authActionKind,
  authActionNeedsReason,
  authActionTitle,
  authActionTone,
  reasonLabel,
  reasonPlaceholder,
  type AuthActionMeta,
  type AuthActionTone,
} from './auth-action'

/**
 * The step-up dialog, ported from legacy `components/employee-auth-modal.tsx`.
 *
 * It asks for somebody's credentials and a reason, and then performs the action
 * itself — it is not a confirmation dialog that hands back a boolean. That is
 * the point: the server re-authenticates the person confirming and records who
 * they were, so the four actions it drives are attributable to a human rather
 * than to whoever happened to be logged in at the till.
 *
 * Which of the four it performs is decided in `auth-action.ts` and tested
 * there, because `return-to-kitchen` moves a driver's wallet credit (§14.5).
 *
 * It lives in `packages/shared` because three screens across two modules open it —
 * the order archive, the restaurant till and the fiscal receipts — and §5 forbids
 * one module importing another. It is allowed here because shared may reach the
 * API client, which `packages/ui` may not (§7).
 */

const TONE_ICONS: Record<AuthActionTone, typeof CheckCircle2> = {
  reopen: RotateCcw,
  destructive: Ban,
  quality: Star,
  confirm: CheckCircle2,
}

const TONE_ICON_COLORS: Record<AuthActionTone, string> = {
  reopen: 'text-amber-500',
  destructive: 'text-red-500',
  quality: 'text-cyan-500',
  confirm: 'text-emerald-500',
}

const TONE_ICON_BACKGROUNDS: Record<AuthActionTone, string> = {
  reopen: 'bg-amber-500/15',
  destructive: 'bg-red-500/15',
  quality: 'bg-cyan-500/15',
  confirm: 'bg-emerald-500/15',
}

const TONE_BUTTONS: Record<AuthActionTone, string> = {
  reopen: 'bg-amber-500 hover:bg-amber-600',
  destructive: 'bg-red-500 hover:bg-red-600',
  quality: 'bg-cyan-500 hover:bg-cyan-600',
  confirm: 'bg-emerald-500 hover:bg-emerald-600',
}

const FIELD_CLASSES =
  'w-full px-3 py-2.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

/** The role wording for a re-open, which has no entry in the role matrix. */
const REOPEN_ROLES = ['main_admin', 'admin', tt('alebo povolenie „Vrátiť do kuchyne“')]

/** The bridge answers this when its build predates the storno command. */
const BRIDGE_OUTDATED = 'BRIDGE_OUTDATED'

/**
 * Whatever the endpoint sent back; the caller reads what it needs.
 *
 * `object` rather than an index signature: once these responses were typed on the backend they
 * became records, and a record has no index signature, so `{ [key: string]: unknown }` stopped
 * accepting them. No caller reads a field off this — the name comes through the first argument —
 * so the loosest honest type is the one that says "some response came back".
 */
export type AuthSuccessPayload = object

export function EmployeeAuthModal({
  meta,
  onClose,
  onSuccess,
  verifyOnly,
}: {
  meta: AuthActionMeta
  onClose: () => void
  onSuccess: (confirmedByName: string, response?: AuthSuccessPayload) => void
  /**
   * Check the credentials and nothing else — the caller applies the change. Used
   * for the two admin overrides, which are ordinary PATCHes behind a password.
   */
  verifyOnly?: boolean
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [bridgeOutdated, setBridgeOutdated] = useState(false)

  const kind = authActionKind(meta, verifyOnly)
  const tone = authActionTone(meta)
  const needsReason = authActionNeedsReason(meta)
  const ActionIcon = TONE_ICONS[tone]
  const allowedRoles = meta.returnToKitchen
    ? REOPEN_ROLES
    : (ROLES_THAT_MAY_CONFIRM[meta.newStatus ?? ''] ?? [])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!email.trim() || !password.trim()) {
      setError(tt('Zadajte email a heslo'))
      return
    }
    if (needsReason && !reason.trim()) {
      setError(tt('Zadajte dôvod zmeny'))
      return
    }

    setIsSubmitting(true)
    setError('')
    setBridgeOutdated(false)
    try {
      const response = await perform()
      onSuccess(confirmerName(response), response)
    } catch (caught) {
      if (isApiError(caught) && caught.code === BRIDGE_OUTDATED) {
        setBridgeOutdated(true)
        return
      }
      setError(apiErrorMessage(caught, tt('Chyba overenia')))
    } finally {
      setIsSubmitting(false)
    }
  }

  const perform = async (): Promise<AuthSuccessPayload> => {
    if (kind === 'verify-only') {
      // Spread, because `postAuthLogin` is typed now (BLOCKED #4) and a named interface is not
      // assignable to an index signature. That is TypeScript saying something true: the four
      // branches below answer four different shapes, and this modal reads whichever field names
      // the confirming employee. Spreading states that the response is being treated as a bag of
      // fields, rather than pretending `LoginResponse` is one.
      return { ...(await postAuthLogin({ email, password })) }
    }
    if (kind === 'fiskal-storno') {
      return postFiskalStorno({
        orderId: meta.orderId,
        employeeEmail: email,
        employeePassword: password,
      })
    }
    /* §9.687 - closing a table with no fiscal document used to be a branch here (§9.468). Owner #116
     * (O64) removed its login, so `PaymentDialog` posts it itself and this prompt never sees it. */
    if (kind === 'return-to-kitchen') {
      return postOrdersOrderIdReturnToKitchen(meta.orderId, { email, password, reason })
    }
    return postOrdersOrderIdConfirmStatus(meta.orderId, {
      email,
      password,
      newStatus: meta.newStatus ?? '',
      reason,
    })
  }

  /**
   * Each endpoint names the confirming employee differently, and the caller
   * puts that name in a toast the manager reads as the audit record. Falling
   * back to the typed email is better than an empty "Overil: ".
   */
  const confirmerName = (response: AuthSuccessPayload): string => {
    const field =
      kind === 'verify-only' ? 'name' : kind === 'fiskal-storno' ? 'employee' : 'confirmedByName'
    // The three endpoints put the name under three different keys, so this reads one of them by
    // name. A cast, because `AuthSuccessPayload` is now `object`: the responses became records when
    // the backend typed them, and a record has no index signature to read through.
    const value = (response as Record<string, unknown>)[field]
    return typeof value === 'string' && value !== '' ? value : email
  }

  /* §9.477 - §9.441's three keyboard behaviours, which this overlay's `aria-modal` promised and did
     not keep. Rendered only when open, so `open` is a constant `true` here. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, isSubmitting ? null : onClose)

  return (
    /* §9.467 - the prompt names itself. It stands in front of every sensitive money action in this app -
     * a payment-method change, a storno, a direct patch, a close with no document - and **no e2e had
     * ever driven it**, which is partly because it could not be found: no marker, and both it and the
     * row behind it carry a *Potvrdiť* button. */
    <div
      data-employee-auth
      role="dialog"
      aria-modal="true"
      /* §9.467 - it names itself to a screen reader too. Without `role`/`aria-modal` the content
       * behind stays in the reading order while the screen is dimmed and every click is swallowed,
       * and without a name the person authorising a money action cannot hear which one. */
      aria-labelledby="employee-auth-title"
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      {/* §9.477 - the card rather than the scrim above it, which has no focusable children. */}
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl"
      >
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
          <div
            className={cn(
              'w-9 h-9 rounded-xl flex items-center justify-center',
              TONE_ICON_BACKGROUNDS[tone],
            )}
          >
            <ActionIcon className={cn('w-5 h-5', TONE_ICON_COLORS[tone])} />
          </div>
          <div className="min-w-0">
            {/* An `<h2>` rather than a `<p>`: `aria-labelledby` naming a paragraph is a name with
             * no heading behind it, and this title is the first thing a screen reader must reach.
             * The classes are unchanged, so nothing on the screen moves. */}
            <h2 id="employee-auth-title" className="font-bold text-foreground truncate">
              <T>Potvrdiť:</T> {authActionTitle(meta)}
            </h2>
            <p className="text-xs text-muted-foreground">
              #{meta.orderNumber} - {formatCents(meta.orderTotalCents)}
            </p>
          </div>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            /* §9.477 - where the opening focus lands when nothing above it is a field. This prompt
               always has a password field, so in practice the field wins; the marker is what keeps
               that true if a mode of it ever renders without one. */
            data-dialog-cancel
            className="ml-auto p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={(event) => void submit(event)} className="p-6 space-y-4">
          <div className="bg-muted/40 rounded-xl p-3 text-xs text-muted-foreground space-y-1">
            <p className="font-semibold text-foreground">
              <T>Overenie zamestnanca</T>
            </p>
            <p>
              <T>Táto akcia vyžaduje potvrdenie zamestnancom s oprávnením:</T>{' '}
              <span className="text-primary font-semibold">{allowedRoles.join(', ')}</span>
            </p>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="employee-auth-email"
              className="text-xs font-bold text-muted-foreground uppercase tracking-wider"
            >
              Email
            </label>
            <input
              id="employee-auth-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="vas@email.sk"
              autoComplete="username"
              className={FIELD_CLASSES}
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="employee-auth-password"
              className="text-xs font-bold text-muted-foreground uppercase tracking-wider"
            >
              <T>Heslo</T>
            </label>
            <div className="relative">
              <input
                id="employee-auth-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className={cn(FIELD_CLASSES, 'pr-10')}
              />
              <button
                type="button"
                aria-label={tt('Zobraziť heslo')}
                onClick={() => setShowPassword((shown) => !shown)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {needsReason && (
            <div className="space-y-1">
              <label
                htmlFor="employee-auth-reason"
                className="text-xs font-bold text-muted-foreground uppercase tracking-wider"
              >
                {reasonLabel(meta)}
              </label>
              <textarea
                id="employee-auth-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={reasonPlaceholder(meta)}
                rows={3}
                className={cn(FIELD_CLASSES, 'resize-none')}
              />
            </div>
          )}

          {bridgeOutdated && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl px-4 py-3 space-y-2">
              <div className="flex items-start gap-2 text-orange-400">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-semibold">
                  <T>Zastaralá verzia bridge</T>
                </p>
              </div>
              <p className="text-sm text-orange-300 leading-relaxed">
                <T>
                  Vaša verzia bridge nepodporuje storno. Stiahnite novú verziu a reštartujte bridge
                  na Windows PC.
                </T>
              </p>
              {/* Legacy builds this href out of BASE_URL by hand and reloads the
                  app to follow it. wouter's Link already knows the base. */}
              <Link
                href="/settings/fiskal"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold text-orange-200 bg-orange-500/20 hover:bg-orange-500/30 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                <T>Nastavenia → Pokladňa (stiahnuť bridge)</T>
              </Link>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5 text-red-500 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-border text-muted-foreground font-bold text-sm hover:bg-muted transition-colors"
            >
              <T>Zrušiť</T>
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={cn(
                'flex-1 py-2.5 rounded-xl text-white font-bold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50',
                TONE_BUTTONS[tone],
              )}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ActionIcon className="w-4 h-4" />
              )}
              <T>Potvrdiť</T>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
